import { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import RuleEngineBackend from "../../services/RuleEngineBackend";
import PPUtils from "../../../utils/PPUtils";
import { toast } from "react-toastify";

export default function PPPrioritySelectionModal({ visible, close, onSelect, currentRule = null, isNewRule = false, channel = null }) {
     const [rules, setRules] = useState([]);
     const [isLoading, setIsLoading] = useState(false);

     const auth = PPUtils.getLogin();
     const token = auth.token;
     const customerId = PPUtils.getEffectiveCustomerId(auth);

     useEffect(() => {
          if (visible) {
               fetchRules();
          }
     }, [visible, currentRule, isNewRule, channel]);

     const fetchRules = async () => {
          setIsLoading(true);
          try {
               if (!channel?.id) {
                    setIsLoading(false);
                    toast.warning("Bitte wählen Sie zuerst einen Kanal aus");
                    return;
               }

               const rulesData = await RuleEngineBackend.getAllRules(customerId, token);
               if (rulesData) {
                    let processedRules = rulesData.map(rule => ({ ...rule, isCurrentRule: false }));

                    processedRules = processedRules.filter(rule =>
                         rule.channel && rule.channel.id === channel.id
                    );

                    processedRules.sort((a, b) => {
                         const priorityA = a.rulePriority || 0;
                         const priorityB = b.rulePriority || 0;
                         return priorityA - priorityB;
                    });

                    let initialPosition = 0;

                    if (isNewRule && !currentRule) {
                         const dummyRule = {
                              id: 'new-rule-dummy',
                              name: 'Neue Regel',
                              description: 'Ihre neue Regel',
                              rulePriority: 0,
                              isCurrentRule: true
                         };
                         processedRules.unshift(dummyRule);
                         initialPosition = 0;
                    } else if (currentRule) {
                         const existingIndex = processedRules.findIndex(rule => rule.id === currentRule.id);

                         if (existingIndex !== -1) {
                              initialPosition = existingIndex;
                         } else {
                              // If the current rule is not part of the loaded list,
                              // add a synthetic entry at the top so the user can still position it.
                              const syntheticRule = {
                                   id: currentRule.id,
                                   name: currentRule.name || 'Aktuelle Regel',
                                   description: currentRule.description || '',
                                   rulePriority: 0,
                                   isCurrentRule: true
                              };
                              processedRules.unshift(syntheticRule);
                              initialPosition = 0;
                         }
                    }

                    processedRules = processedRules.map((rule, index) => ({
                         ...rule,
                         isCurrentRule: index === initialPosition
                    }));

                    setRules(processedRules);

                    setTimeout(() => {
                         scrollToCurrentRule(initialPosition);
                    }, 100);
               }
          } catch (error) {
               console.error("Error fetching rules:", error);
               toast.error("Fehler beim Laden der Regeln");
          } finally {
               setIsLoading(false);
          }
     };

     const scrollToCurrentRule = (position) => {
          const container = document.querySelector('[data-priority-list]');
          if (container) {
               const ruleElement = container.children[position];
               if (ruleElement) {
                    ruleElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
               }
          }
     };

     const getCurrentIndex = () => rules.findIndex(rule => rule.isCurrentRule);

     const moveUp = () => {
          const currentPosition = getCurrentIndex();
          if (currentPosition > 0) {
               const targetIndex = currentPosition - 1;

               setRules(prevRules => {
                    const newRules = prevRules.map(rule => ({ ...rule }));
                    const [movedRule] = newRules.splice(currentPosition, 1);
                    newRules.splice(targetIndex, 0, movedRule);

                    return newRules.map((rule, index) => ({
                         ...rule,
                         isCurrentRule: index === targetIndex
                    }));
               });

               setTimeout(() => {
                    scrollToCurrentRule(targetIndex);
               }, 50);
          }
     };

     const moveDown = () => {
          const currentPosition = getCurrentIndex();
          if (currentPosition < rules.length - 1) {
               const targetIndex = currentPosition + 1;

               setRules(prevRules => {
                    const newRules = prevRules.map(rule => ({ ...rule }));
                    const [movedRule] = newRules.splice(currentPosition, 1);
                    newRules.splice(targetIndex, 0, movedRule);

                    return newRules.map((rule, index) => ({
                         ...rule,
                         isCurrentRule: index === targetIndex
                    }));
               });

               setTimeout(() => {
                    scrollToCurrentRule(targetIndex);
               }, 50);
          }
     };

     const handleSave = () => {
          const currentIndex = getCurrentIndex();
          const newPriority = (currentIndex >= 0 ? currentIndex : 0) + 1;
          onSelect(newPriority);
          close();
     };

     const channelName = channel?.channelName || 'Kanal';

     return (
          <Dialog
               header={`Priorität wählen - ${channelName}`}
               visible={visible}
               style={{ width: '800px' }}
               onHide={close} footer={
                    <div className="d-flex justify-content-between align-items-center">
                         <button className="btn btn-secondary" onClick={close}>
                              Abbrechen
                         </button>
                         <button className="btn btn-primary" onClick={handleSave}>
                              Position bestätigen
                         </button>
                    </div>
               }
          >
               {isLoading ? (
                    <div className="text-center py-4">
                         <output className="spinner-border">
                              <span className="visually-hidden">Loading...</span>
                         </output>
                         <div className="mt-2">Regeln werden geladen...</div>
                    </div>
               ) : (
                    <div>
                         <p className="mb-3 text-muted">
                              {isNewRule ? 'Positionieren Sie Ihre neue Regel' : 'Positionieren Sie Ihre Regel'} in der Prioritätsliste für den Kanal <strong>{channelName}</strong>. Verwenden Sie die Pfeiltasten, um die Position zu ändern.
                         </p>

                         {/* Move controls */}
                         <div className="mb-3 d-flex justify-content-center gap-2">
                              <button
                                   className="btn btn-outline-primary"
                                   onClick={moveUp}
                                   disabled={getCurrentIndex() <= 0}
                                   title="Nach oben bewegen"
                              >
                                   <i className="fas fa-arrow-up"></i> Nach oben
                              </button>
                              <button
                                   className="btn btn-outline-primary"
                                   onClick={moveDown}
                                   disabled={getCurrentIndex() === -1 || getCurrentIndex() === rules.length - 1}
                                   title="Nach unten bewegen"
                              >
                                   <i className="fas fa-arrow-down"></i> Nach unten
                              </button>
                         </div>

                         <div className="border rounded p-3" style={{ maxHeight: '400px', overflowY: 'auto' }} data-priority-list>
                              {rules.length === 0 ? (
                                   <div className="text-center text-muted py-4">
                                        Keine Regeln gefunden
                                   </div>
                              ) : (
                                   rules.map((rule, index) => (
                                        <div key={`${rule.id}-${index}`} className="mb-2" data-rule-index={index}>
                                             <div className={`card ${rule.isCurrentRule ? 'border-primary border-3' : 'border-light'}`}>
                                                  <div className="card-body py-2">
                                                       <div className="d-flex align-items-center">
                                                            <div className="me-3">
                                                                 <span className={`fw-bold ${rule.isCurrentRule ? 'text-primary' : 'text-muted'}`}>
                                                                      Position: {index + 1}
                                                                 </span>
                                                            </div>
                                                            <div className="flex-grow-1">
                                                                 <div className={`fw-bold ${rule.isCurrentRule ? 'text-primary' : ''}`}>
                                                                      {rule.name}
                                                                 </div>
                                                                 {rule.description && (
                                                                      <small className="text-muted d-block">
                                                                           {rule.description}
                                                                      </small>
                                                                 )}
                                                            </div>
                                                       </div>
                                                  </div>
                                             </div>
                                        </div>
                                   ))
                              )}
                         </div>
                    </div>
               )}
          </Dialog>
     );
}
